import { describeApiError } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { isGone, standingsNotice } from '../leaderboards/notice';
import { usePlayer } from '../player';
import { useScreenViewed } from '../api';
import { useLeaderboards, useLeaderboardStandings } from '../queries';
import { LeaderboardBreakdown } from './LeaderboardBreakdown';

export function LeaderboardsView() {
  const { player, room, theme } = usePlayer();
  useScreenViewed('leaderboards', player?.roomId ?? null);
  const leaderboardsQ = useLeaderboards(room?.id ?? null);
  const leaderboards = leaderboardsQ.data ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // §7.4. Which player's breakdown is open, if any. One at a time: two expanded rows push the
  // rest of a long board off the screen, and the question this answers is about one person.
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);

  const activeId = selectedId ?? leaderboards[0]?.id ?? null;
  const standingsQ = useLeaderboardStandings(activeId);

  // §7.5: these are the latest snapshot, not a live figure. A player's standing therefore only
  // moves when the room recalculates — which is the point, and why the header says when.
  const standings = standingsQ.data?.data ?? [];
  const published = standingsQ.data?.sequenceNumber ?? null;
  const active = leaderboards.find((l) => l.id === activeId);

  // A 404 is a board that is gone (#237), whatever was read before — so the list is asked again and its tab goes. The
  // reader stays on their choice, so the sentence saying so stays on screen until they pick another board. Any other
  // failed refetch keeps standings already on screen (`standingsNotice` decides both).
  const notice = standingsNotice(
    { error: standingsQ.error, hasData: standingsQ.data !== undefined, published, rows: standings.length },
    describeApiError,
  );
  const qc = useQueryClient();
  const roomId = room?.id ?? null;
  const gone = isGone(standingsQ.error);
  useEffect(() => {
    if (gone && roomId) void qc.invalidateQueries({ queryKey: ['leaderboards', roomId] });
  }, [gone, roomId, qc]);

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  if (leaderboardsQ.isLoading) return <Spinner label="Loading leaderboards…" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Leaderboards</h1>

      {leaderboards.length === 0 ? (
        <p style={card} className="rounded-xl p-8 text-center text-sm" >
          <span style={{ color: theme.textMuted }}>No leaderboards are running at your room yet.</span>
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {leaderboards.map((lb) => (
              <button
                key={lb.id}
                type="button"
                onClick={() => {
                  setSelectedId(lb.id);
                  // A rank means nothing across two boards, so an expansion cannot survive the
                  // switch — it would be one player's row on a board they may not even be on.
                  setOpenPlayerId(null);
                }}
                className="rounded-lg px-3 py-2 text-sm font-medium transition"
                style={{
                  background: lb.id === activeId ? `${theme.accent}1a` : theme.panel,
                  color: lb.id === activeId ? theme.accent : theme.textMuted,
                  border: `1px solid ${lb.id === activeId ? theme.accent : theme.border}`,
                }}
              >
                {lb.name}
              </button>
            ))}
          </div>

          <section style={card} className="overflow-hidden rounded-xl">
            <header className="p-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
                {active?.name}
              </h2>
              {active ? (
                <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
                  {active.dateStart} — {active.dateEnd}
                  {published !== null && standingsQ.data?.computedAt
                    ? ` · updated ${formatWhen(standingsQ.data.computedAt)}`
                    : ''}
                  {standingsQ.data?.isFinal ? ' · final' : ''}
                </p>
              ) : null}
              {standings.length > 0 ? (
                // §7.4 is only worth building if somebody finds it. A chevron on each row is the
                // affordance; this is the one sentence that says what is behind it.
                <p className="mt-2 text-xs" style={{ color: theme.textMuted }}>
                  Choose a player to see how their points were worked out.
                </p>
              ) : null}
            </header>

            {standingsQ.isLoading ? (
              <Spinner label="Loading standings…" />
            ) : notice !== null ? (
              // Not published, nobody scored, gone, or not loaded: four causes, four sentences. "No standings yet"
              // alone tells a player nothing about whether to keep checking back.
              <p className="p-8 text-center text-sm" style={{ color: theme.textMuted }}>
                {notice}
              </p>
            ) : (
              <ul>
                {standings.map((s) => {
                  const isMe = s.player.id === player?.id;
                  const isOpen = openPlayerId === s.player.id;
                  return (
                    <li
                      key={s.player.id}
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        background: isMe ? `${theme.accent}12` : 'transparent',
                      }}
                    >
                      {/*
                        The whole row opens §7.4's breakdown. It is a button rather than a link
                        because neither this app nor room-web routes by URL, and it is the whole
                        row rather than a separate "why?" control because on a phone the row is
                        already the width of the screen — a second target beside it would be the
                        smaller of the two.

                        Nothing about the control is orange. On this side of the glass the signal
                        means "this is you" and never "act here", so the affordance is the chevron
                        in chalk; the orange tint on this row says whose it is, which is exactly
                        what it is supposed to say.
                      */}
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => setOpenPlayerId(isOpen ? null : s.player.id)}
                        // The hover tint is white rather than the accent: on this ground the
                        // signal orange means "this is you", and lighting a row up in it as the
                        // pointer crosses would say that about whoever it happened to be over.
                        className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-white/5"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span
                            className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                            style={{
                              background: s.rank === 1 ? theme.accent : theme.border,
                              color: s.rank === 1 ? theme.bg : theme.text,
                            }}
                          >
                            {/* The snapshot's rank, not the row number: tied players share a rank
                                and the next one skips (1, 2, 2, 4). */}
                            {s.rank}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-semibold">
                              {s.player.name}
                              {isMe ? (
                                <span className="ml-2 text-xs uppercase tracking-wider" style={{ color: theme.accent }}>
                                  You
                                </span>
                              ) : null}
                            </div>
                            <div className="text-xs" style={{ color: theme.textMuted }}>
                              {s.eventsScored} event{s.eventsScored === 1 ? '' : 's'} scored
                              {s.isTied ? ' · tied' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <div className="text-right">
                            <div className="font-bold" style={{ color: theme.accent }}>
                              {trimPoints(s.points)}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
                              pts
                            </div>
                          </div>
                          <span
                            aria-hidden
                            className="text-xs transition-transform"
                            style={{
                              color: theme.textMuted,
                              transform: isOpen ? 'rotate(90deg)' : 'none',
                            }}
                          >
                            ▶
                          </span>
                        </div>
                      </button>

                      {isOpen && activeId ? (
                        <LeaderboardBreakdown
                          leaderboardId={activeId}
                          playerId={s.player.id}
                          playersRanked={standings.length}
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * Points are stored to four decimal places and most systems produce round numbers, so `90.0000` is
 * noise on every row. Trailing zeroes go; real precision stays.
 */
function trimPoints(points: number): string {
  return String(Math.round(points * 10000) / 10000);
}

/** An ISO timestamp as something a player reads. Falls back to the raw value rather than NaN. */
function formatWhen(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
